import React from 'react';
import { Badge } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { SxProps, Theme } from '@mui/material/styles';
import { ICompany } from '../../redux/company/types';
import { IHost, IPartner } from '../../redux/discovery/types';

export interface IAvatar {
    host: IHost | IPartner;
    sx?: SxProps<Theme>;
    onClick?: (company?: ICompany) => void;
    enableDefaultNavigation?: boolean;
    badgeSx?: SxProps<Theme>;
    disableVerified?: boolean;
    size?: 'large' | 'medium' | 'small'; // New size prop
}

function stringToColor(string: string) {
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}

function stringAvatar(name: string, sx?: SxProps<Theme>) {
    if (!name || typeof name !== 'string' || name.trim().split(' ').length < 2) {
        // Handle invalid name or insufficient words (e.g., single name)
        name = 'Unknown User'; // or any default name you prefer
    }

    return {
        sx: {
            bgcolor: stringToColor(name),
            fontSize: 12,
            ...sx,
        },
        children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
    };
}

const HostOrBrandAvatar: React.FC<IAvatar> = ({
    host,
    sx,
    badgeSx,
    size = 'large', // default is 'large'
}) => {
    const upperCaseFirstName = host.name;

    // Apply scale based on size
    const getAvatarDimensions = () => {
        const base = 32; // assumed default size (large)
        switch (size) {
            case 'medium':
                return { width: base * 0.8, height: base * 0.8 };
            case 'small':
                return { width: base * 0.5, height: base * 0.5 };
            default:
                return { width: base, height: base };
        }
    };

    const avatarSizeStyle = getAvatarDimensions();

    return (
        <Badge overlap="circular" sx={badgeSx} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Avatar
                alt={`${upperCaseFirstName}`}
                src={host?.profile_picture || ''}
                {...stringAvatar(`${upperCaseFirstName}`, {
                    ...sx,
                    ...avatarSizeStyle,
                })}>
                {upperCaseFirstName?.charAt(0)}
            </Avatar>
        </Badge>
    );
};

export { HostOrBrandAvatar };
